import { ApiError } from '@/api/client';
import type { CandidateListItem } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useDeleteCandidates } from '../api';

export function CandidateDeleteDialog({
  candidates,
  onClose,
  onSuccess,
}: {
  candidates: CandidateListItem[];
  onClose: () => void;
  onSuccess: (affected: number) => void;
}) {
  const mutation = useDeleteCandidates();
  const toast = useToast();
  const close = () => {
    if (!mutation.isPending) onClose();
  };
  const countLabel = `${candidates.length} ${candidates.length === 1 ? 'candidate' : 'candidates'}`;
  return (
    <Modal
      open
      title={`Delete ${countLabel}?`}
      tone="danger"
      onClose={close}
      description="These candidates will be removed from active CRM lists. Their records, documents and application history will be retained."
      footer={
        <>
          <Button variant="ghost" disabled={mutation.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                { candidate_ids: candidates.map((candidate) => candidate.id) },
                {
                  onSuccess: ({ affected }) => {
                    toast.success(
                      `${affected} ${affected === 1 ? 'candidate' : 'candidates'} deleted.`,
                    );
                    onSuccess(affected);
                    onClose();
                  },
                },
              );
            }}
          >
            Delete {countLabel}
          </Button>
        </>
      }
    >
      <ul>
        {candidates.map((candidate) => (
          <li key={candidate.id}>
            <strong>{candidate.full_name}</strong> ({candidate.reference})
          </li>
        ))}
      </ul>
      {mutation.isError ? (
        <Alert tone="danger">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'The candidates could not be deleted. Refresh the list before trying again.'}
        </Alert>
      ) : null}
    </Modal>
  );
}
